import type { FC, ReactNode } from "react";

import { useOverlay } from "../hooks/useOverlay";

export interface MobileMenuProps {
  /**
   * Whether the menu is open
   */
  isOpen: boolean;

  /**
   * Callback to close the menu
   */
  onClose: () => void;

  /**
   * Menu content
   */
  children: ReactNode;
}

/**
 * MobileMenu Component
 *
 * A slide-in mobile menu with backdrop overlay.
 * Includes proper accessibility (focus trap, ESC key, click outside).
 *
 * @example
 * ```tsx
 * <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
 *   <nav>...</nav>
 * </MobileMenu>
 * ```
 */
export const MobileMenu: FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  children,
}) => {
  useOverlay(isOpen, onClose);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden dark:bg-black/60 ${isOpen ? "opacity-100" : "pointer-events-none opacity-0"} `
          .trim()
          .replace(/\s+/g, " ")}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-in Menu */}
      <aside
        className={`ease-smooth fixed inset-y-0 left-0 z-50 flex w-80 max-w-[85vw] flex-col justify-between overflow-hidden border-r-2 border-slate-300 bg-slate-100 px-5 py-6 text-sm text-slate-600 shadow-2xl transition-transform duration-300 lg:hidden dark:border-slate-700 dark:bg-arti-panel dark:text-slate-300 dark:shadow-[10px_0_30px_rgba(0,0,0,0.5)] ${isOpen ? "translate-x-0" : "-translate-x-full"} `
          .trim()
          .replace(/\s+/g, " ")}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-none border border-slate-300 bg-white text-gray-600 transition-colors hover:border-slate-400 hover:bg-slate-100 hover:text-gray-900 dark:border-slate-700 dark:bg-arti-background dark:text-white/60 dark:hover:border-slate-600 dark:hover:bg-white/10 dark:hover:text-white"
          aria-label="Close menu"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {children}
      </aside>
    </>
  );
};

/**
 * HamburgerButton Component
 *
 * Animated hamburger menu button.
 *
 * @example
 * ```tsx
 * <HamburgerButton isOpen={isMenuOpen} onClick={() => setIsMenuOpen(!isMenuOpen)} />
 * ```
 */
export interface HamburgerButtonProps {
  /**
   * Whether the menu is open (animates to X icon)
   */
  isOpen: boolean;

  /**
   * Click handler
   */
  onClick: () => void;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export const HamburgerButton: FC<HamburgerButtonProps> = ({
  isOpen,
  onClick,
  className = "",
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex h-8 w-8 flex-col items-center justify-center rounded-none border border-slate-300 bg-white transition-colors hover:border-slate-400 hover:bg-slate-100 lg:hidden dark:border-slate-700 dark:bg-arti-panel dark:hover:border-slate-600 dark:hover:bg-arti-background ${className} `
      .trim()
      .replace(/\s+/g, " ")}
    aria-label={isOpen ? "Close menu" : "Open menu"}
    aria-expanded={isOpen}
  >
    <span className="flex h-5 w-6 flex-col items-center justify-center gap-1.5">
      <span
        className={`h-0.5 w-full bg-gray-700 transition-all duration-300 dark:bg-white ${isOpen ? "translate-y-2 rotate-45" : ""} `
          .trim()
          .replace(/\s+/g, " ")}
      />
      <span
        className={`h-0.5 w-full bg-gray-700 transition-all duration-300 dark:bg-white ${isOpen ? "opacity-0" : ""} `
          .trim()
          .replace(/\s+/g, " ")}
      />
      <span
        className={`h-0.5 w-full bg-gray-700 transition-all duration-300 dark:bg-white ${isOpen ? "-translate-y-2 -rotate-45" : ""} `
          .trim()
          .replace(/\s+/g, " ")}
      />
    </span>
  </button>
);
